import React, { useEffect, useState } from "react";
import axios from "axios";
import CustomPagination from "../../components/Pagination";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { formatDate, trimAddress, copyToClipboard } from "../../helper/helper";
import { IoCopyOutline } from "react-icons/io5";
import Loader from "../../components/Loader";

const UserWithdrawalsAdmin = () => {
    const [withdrawals, setWithdrawals] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [merchantFilter, setMerchantFilter] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const WithdrawalStatus = {
        "": "All Status",
        "PENDING": "Pending",
        "AUTO_APPROVED": "Auto Approved",
        "APPROVED": "Approved",
        "PROCESSING": "Processing",
        "SUCCESS": "Success",
        "FAILED": "Failed",
        "DECLINED": "Declined",
    };

    // Fetch all merchants for filter
    const fetchMerchants = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/merchants/list`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    params: {
                        pageNo: 1,
                        limitVal: 100,
                    },
                }
            );
            setMerchants(response?.data?.data || []);
        } catch (error) {
            console.error("Error fetching merchants:", error);
        }
    };

    // Fetch all withdrawals
    const fetchWithdrawals = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/user-withdrawal/admin/list`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    params: {
                        pageNo: currentPage,
                        limitVal: 10,
                        search: searchQuery || undefined,
                        status: statusFilter || undefined,
                        merchantId: merchantFilter || undefined,
                    },
                }
            );
            setWithdrawals(response?.data?.data || []);
            setTotalPages(response?.data?.totalPages || 1);
        } catch (error) {
            console.error("Error fetching withdrawals:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchMerchants();
    }, []);

    useEffect(() => {
        // Debounce search to avoid API calls on every keystroke
        const delayTimer = setTimeout(() => {
            fetchWithdrawals();
        }, 500); // 500ms delay

        return () => clearTimeout(delayTimer);
    }, [currentPage, searchQuery, statusFilter, merchantFilter]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1);
    };

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleMerchantChange = (e) => {
        setMerchantFilter(e.target.value);
        setCurrentPage(1);
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            PENDING: { bg: "#ffc107", color: "#000" },
            AUTO_APPROVED: { bg: "#17a2b8", color: "#fff" },
            APPROVED: { bg: "#28a745", color: "#fff" },
            PROCESSING: { bg: "#007bff", color: "#fff" },
            SUCCESS: { bg: "#198754", color: "#fff" },
            FAILED: { bg: "#dc3545", color: "#fff" },
            DECLINED: { bg: "#6c757d", color: "#fff" },
        };

        const style = statusColors[status] || { bg: "#6c757d", color: "#fff" };

        return (
            <span
                style={{
                    backgroundColor: style.bg,
                    color: style.color,
                    padding: "4px 10px",
                    borderRadius: "15px",
                    fontSize: "12px",
                    fontWeight: "500",
                }}
            >
                {status}
            </span>
        );
    };

    if (isLoading) {
        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px"
            }}>
                <Loader />
            </div>
        );
    }

    return (
        <div>
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0">User Withdrawals Management</h1>
                            </div>
                        </div>
                    </div>

                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div
                                        className="card-body"
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        {/* Filters */}
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                marginBottom: "15px",
                                                flexWrap: "wrap",
                                                gap: "10px",
                                            }}
                                        >
                                            <div style={{ position: "relative", flex: 1, maxWidth: "400px" }}>
                                                <input
                                                    style={{
                                                        width: "100%",
                                                        padding: "8px 35px 8px 12px",
                                                        borderRadius: "5px",
                                                        backgroundColor: "#f5f5f5",
                                                        border: "1px solid #ccc",
                                                    }}
                                                    type="text"
                                                    placeholder="Search by username, email, userId, wallet, txHash, reference, or amount..."
                                                    value={searchQuery}
                                                    onChange={handleSearchChange}
                                                />
                                                {isLoading && (
                                                    <div style={{
                                                        position: "absolute",
                                                        right: "10px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                    }}>
                                                        <i className="fas fa-spinner fa-spin"></i>
                                                    </div>
                                                )}
                                            </div>

                                            <div style={{ display: "flex", gap: "10px" }}>
                                                <select
                                                    style={{
                                                        width: "150px",
                                                        height: "40px",
                                                        padding: "5px 10px",
                                                        borderRadius: "5px",
                                                        backgroundColor: "#f5f5f5",
                                                        border: "1px solid #ccc",
                                                    }}
                                                    value={statusFilter}
                                                    onChange={handleStatusChange}
                                                    className="form-select"
                                                >
                                                    {Object.entries(WithdrawalStatus).map(([key, value]) => (
                                                        <option key={key} value={key}>
                                                            {value}
                                                        </option>
                                                    ))}
                                                </select>

                                                <select
                                                    style={{
                                                        width: "200px",
                                                        height: "40px",
                                                        padding: "5px 10px",
                                                        borderRadius: "5px",
                                                        backgroundColor: "#f5f5f5",
                                                        border: "1px solid #ccc",
                                                    }}
                                                    value={merchantFilter}
                                                    onChange={handleMerchantChange}
                                                    className="form-select"
                                                >
                                                    <option value="">All Merchants</option>
                                                    {merchants.map((merchant) => (
                                                        <option key={merchant._id} value={merchant._id}>
                                                            {merchant.name || merchant.email}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {/* Table */}
                                        <div style={{ overflowX: "auto" }}>
                                            <table className="table table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>SN</th>
                                                        <th>User</th>
                                                        <th>Amount</th>
                                                        <th>Chain</th>
                                                        <th>Wallet Address</th>
                                                        <th>Status</th>
                                                        <th>Tx Hash</th>
                                                        <th>Merchant</th>
                                                        <th>Created At</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {withdrawals?.length ? (
                                                        withdrawals.map((withdrawal, index) => (
                                                            <tr key={withdrawal._id || index}>
                                                                <td>{(currentPage - 1) * 10 + index + 1}</td>
                                                                <td>
                                                                    <div>
                                                                        <strong>{withdrawal.userName || withdrawal.userId}</strong>
                                                                        {withdrawal.userEmail && (
                                                                            <div style={{ fontSize: "12px", color: "#666" }}>
                                                                                {withdrawal.userEmail}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <strong>{withdrawal.amount}</strong> {withdrawal.tokenSymbol}
                                                                    {withdrawal.transactionType === "FIAT" && (
                                                                        <div style={{ fontSize: "11px", color: "#856404", backgroundColor: "#fff3cd", padding: "2px 6px", borderRadius: "4px", display: "inline-block", marginTop: "4px" }}>
                                                                            Fiat Request: {withdrawal.fiatAmount} {withdrawal.fiatCurrency?.toUpperCase()}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>{withdrawal.chainId}</td>
                                                                <td>
                                                                    {trimAddress(withdrawal.walletAddress, 8, 6)}
                                                                    {withdrawal.walletAddress && (
                                                                        <button
                                                                            onClick={() => copyToClipboard(withdrawal.walletAddress)}
                                                                            style={{
                                                                                marginLeft: "5px",
                                                                                border: "none",
                                                                                background: "transparent",
                                                                                cursor: "pointer",
                                                                            }}
                                                                        >
                                                                            <IoCopyOutline />
                                                                        </button>
                                                                    )}
                                                                </td>
                                                                <td>{getStatusBadge(withdrawal.status)}</td>
                                                                <td>
                                                                    {withdrawal.txHash ? (
                                                                        <>
                                                                            {trimAddress(withdrawal.txHash, 6, 4)}
                                                                            <button
                                                                                onClick={() => copyToClipboard(withdrawal.txHash)}
                                                                                style={{
                                                                                    marginLeft: "5px",
                                                                                    border: "none",
                                                                                    background: "transparent",
                                                                                    cursor: "pointer",
                                                                                }}
                                                                            >
                                                                                <IoCopyOutline />
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        "—"
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {withdrawal.merchantId?.name ||
                                                                        withdrawal.merchantId?.email ||
                                                                        "—"}
                                                                </td>
                                                                <td>{formatDate(withdrawal.createdAt)}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="9">
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                        height: "100px",
                                                                    }}
                                                                >
                                                                    <p>No withdrawals found</p>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <CustomPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserWithdrawalsAdmin;
